import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const FallbackUI = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useLanguage();
  const eb = t.appMisc?.errorBoundary || {};
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center px-4">
      <AlertTriangle className="h-12 w-12 text-destructive" />
      <h2 className="text-xl font-bold text-foreground">{eb.title || "Something went wrong"}</h2>
      <p className="text-muted-foreground max-w-md">{eb.desc || "An unexpected error occurred. Please try refreshing the page."}</p>
      <Button onClick={onRetry}>{eb.refresh || "Refresh page"}</Button>
    </div>
  );
};

class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    // No-op if Sentry DSN is not configured; safe to call regardless.
    Sentry.captureException(error, { extra: { errorInfo } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <FallbackUI
          onRetry={() => {
            this.setState({ hasError: false });
            window.location.reload();
          }}
        />
      );
    }
    return this.props.children;
  }
}

export default RouteErrorBoundary;
